import { useMemo } from 'react'

import { useIsMyCalendlyScope } from 'components/meetings/filter_modal'
import { computeStatusDisplayText } from 'components/meetings/utils'
import { useMeetingsContext } from 'features/meetings/contexts/MeetingsContext'
import { useMeetingFromCache } from 'features/meetings/hooks/use_meeting_from_cache'
import { useMeetingsRealtimeFeaturesEnabled } from 'features/meetings/hooks/use_meetings_realtime_features_enabled'
import type { Meeting } from 'features/meetings/types'
import { mapNodeToMeeting } from 'features/meetings/utils'
import { useScope } from 'hooks/meetings/use_scope'
import { useCurrentTime } from 'shared/contexts'
import { getBadgeKey } from 'shared/utils'

/**
 * Computes the live status state for a meeting event.
 *
 * This is a transitional hook that bridges the legacy badge system and the new
 * realtime status system behind the `meetings_recaps_realtime_feature` flag.
 *
 * **Legacy path** (flag off): uses `getBadgeKey` + `useMeetingFromCache` to derive
 * a badge key, rendered by `LegacyStatusBadge` in `MeetingListLiveStatus`.
 *
 * **Realtime path** (flag on): uses `MeetingsContext.getStatus()` to get live
 * meeting status, rendered by `MeetingStatusIndicator` in `MeetingListLiveStatus`.
 *
 * ## Feature flag removal guide (`meetings_recaps_realtime_feature`)
 *
 * When the flag is removed and realtime is the only path:
 *
 * 1. **Delete this hook** (`useMeetingLiveStatus`) entirely.
 * 2. **Delete `MeetingListLiveStatus`** — it only exists to route between paths.
 * 3. **Delete `LegacyStatusBadge`** and its associated `BADGE_CONFIG` / `COLOR_MAP`
 *    constants in `MeetingListLiveStatus.tsx`.
 * 4. **In `MeetingCardContent`**, replace `useMeetingLiveStatus` + `MeetingListLiveStatus`
 *    with a direct call to `MeetingsContext.getStatus()` and render `MeetingStatusIndicator`.
 * 5. **Check for other consumers** of `getBadgeKey`, `useMeetingFromCache`, `useIsMyCalendlyScope`,
 *    and `mapNodeToMeeting` — remove them if no longer referenced elsewhere.
 */
interface MeetingLiveStatusOptions {
  otherEventsOnDay?: Meeting[]
  isPackageBooking?: boolean
}

export const useMeetingLiveStatus = (
  event: Meeting,
  { otherEventsOnDay, isPackageBooking = false }: MeetingLiveStatusOptions = {},
) => {
  // --- Shared state ---
  const meetingsCtx = useMeetingsContext()
  const meetingsRealtimeEnabled = useMeetingsRealtimeFeaturesEnabled()
  const currentTime = useCurrentTime()

  const { scopeType } = useScope()
  const isMyCalendlyScope = useIsMyCalendlyScope()

  // Cache-merge: the legacy path needs a fully resolved event for getBadgeKey.
  // The realtime path only uses resolvedEvent.uuid, so this can be removed with the flag.
  const cachedMeeting = useMeetingFromCache(
    event?.uuid,
    mapNodeToMeeting,
    { scopeType }
  )

  const resolvedEvent = useMemo(() => {
    if (cachedMeeting && cachedMeeting.uuid === event?.uuid) {
      return {
        ...event,
        ...cachedMeeting,
        cancellation: cachedMeeting.cancellation || event.cancellation,
      }
    }
    return event
  }, [cachedMeeting, event])

  // --- Realtime path (keep after flag removal) ---
  if (meetingsCtx?.hasRealtimeFeatures) {
    const meetingStatus = meetingsCtx.getStatus(resolvedEvent.uuid)
    const displayText = meetingStatus
      ? computeStatusDisplayText(meetingStatus.status, meetingStatus.startTime, currentTime)
      : ''

    return {
      hasContent: !!displayText,
      hasRealtimeFeatures: true,
      realtimeStatus: meetingStatus?.status,
      displayText,
      badgeKey: undefined,
      resolvedEvent,
    }
  }

  // --- Legacy path (remove with feature flag) ---
  let badgeKey = getBadgeKey(resolvedEvent, isMyCalendlyScope, { otherEventsOnDay, now: currentTime })
  if (isPackageBooking && badgeKey === 'PAYMENT_ERROR') badgeKey = undefined
  // Hide schedule-overlap conflict in legacy list row when realtime flag is off only;
  // when `meetings_recaps_realtime_feature` is on, list uses realtime path + warning chip instead.
  if (!meetingsRealtimeEnabled && badgeKey === 'CONFLICT') badgeKey = undefined
  // PAST is returned by getBadgeKey but has no visual badge representation
  if (badgeKey === 'PAST') badgeKey = undefined

  return {
    hasContent: !!badgeKey,
    hasRealtimeFeatures: false,
    realtimeStatus: undefined,
    displayText: undefined,
    badgeKey,
    resolvedEvent,
  }
}

export type MeetingLiveStatus = ReturnType<typeof useMeetingLiveStatus>
